<script setup lang="ts">
import device from '@@/bits/device'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsMatrix from '@@/vuecomponents/SurfacePostsMatrix.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { GroupByTypes } from '@padlet/arvo'
import { storeToRefs } from 'pinia'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isPostSizeWide, isPostSizeStandard, isFrozen } = storeToRefs(
  useSurfaceStore(),
)
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { isViewingTranslatedPadlet } = storeToRefs(useSurfaceTranslationsStore())

if (!device.mobile) useDoubleClickToPost()
</script>

<template>
  <div
    id="wall-container"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <SurfacePostsMatrix
      :class="{
        'pb-8 @tablet-portrait/surface-container:pb-12': wishGroupBy === GroupByTypes.Section,
      }"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <div
      v-if="
        wishGroupBy === GroupByTypes.Section && canIModerate && !isReadOnly && !isFrozen && !isViewingTranslatedPadlet
      "
      :class="[
        'flex',
        'flex-col',
        'items-center',
        'mx-auto',
        'box-border',
        'w-full',
        isPostSizeStandard &&
          '@tablet-portrait/surface-container:max-w-[1136px] @tablet-landscape/surface-container:max-w-[1184px] @desktop/surface-container:max-w-[1088px] @tablet-portrait/surface-container:px-6 @tablet-landscape/surface-container:px-12 @desktop/surface-container:px-0',
        isPostSizeWide &&
          '@tablet-portrait/surface-container:max-w-[1236px] @desktop/surface-container:max-w-[1680px] @tablet-portrait/surface-container:px-6 @desktop/surface-container:px-12',
        'pb-16',
      ]"
    >
      <div v-if="!isDemoPadletPanelDesktop" class="w-full">
        <SurfaceAddSectionButton class="ms-4 @tablet-portrait/surface-container:ms-0" />
      </div>
    </div>
  </div>
</template>
